import { reviewRequestError } from '../utils/reviewErrors';

export type CheckStatus = 'safe' | 'unsafe';
export type ReviewStatus = 'pending' | 'approved' | 'skipped' | 'disabled' | 'auto_approved';
export type ReviewAction = 'approved' | 'skipped' | 'disabled';
export type ReasonCode =
  | 'PRICE_INCREASE'
  | 'PRICE_DECREASE'
  | 'CORE_CHARGE_ADDED'
  | 'CORE_CHARGE_REMOVED'
  | 'CORE_CHARGE_CHANGED'
  | 'IS_DISCONTINUED'
  // BOT-377: vendor-portal supersession (fre0/pete) — the page names a successor
  // SKU. Distinct from the CDK_* codes below, which come from the CDK price file.
  | 'IS_SUPERSEDED'
  | 'NO_PRICE_INFO'
  // BOT-384: CDK ABCDEF supersession/obsolescence codes (pacd/cupa) — the part
  // is dead or restricted in the CDK price file while still active in NS.
  | 'CDK_SSD'
  | 'CDK_MSS'
  | 'CDK_NLS'
  | 'CDK_RES'
  | 'CDK_POR'
  | 'CDK_IPL';

export const REASON_CODE_LABELS: Record<ReasonCode, string> = {
  PRICE_INCREASE:      'Price increase',
  PRICE_DECREASE:      'Price decrease',
  CORE_CHARGE_ADDED:   'Core charge added',
  CORE_CHARGE_REMOVED: 'Core charge removed',
  CORE_CHARGE_CHANGED: 'Core charge changed',
  IS_DISCONTINUED:     'Discontinued',
  IS_SUPERSEDED:       'Superseded',
  NO_PRICE_INFO:       'No price information',
  CDK_SSD:             'CDK: Superseded',
  CDK_MSS:             'CDK: Multiple supersession',
  CDK_NLS:             'CDK: No longer supplied',
  // "Reserved", not "Discontinued" — client correction (BOT-384 review): RES
  // parts are alive and buyable under a purchase restriction ("We can still
  // buy it, but only 1 per day", e.g. 1939435PE). Code value stays CDK_RES.
  CDK_RES:             'CDK: Reserved',
  CDK_POR:             'CDK: Special situation',
  CDK_IPL:             'CDK: Moved to Vintage Parts',
};

/** Reason codes that mean "this SKU is dead under this part number" — the rows
 *  where a remanufactured counterpart is the operator's alternative to buy, and
 *  the only ones that surface `vendor_reman_code`.
 *  decision: supersedure — see decisions/supersedure.md */
export const DEAD_PART_REASON_CODES: ReadonlySet<string> = new Set<ReasonCode>([
  'IS_DISCONTINUED',
  'IS_SUPERSEDED',
]);

/** The two vendors whose checks come from the CDK file — the only ones whose
 *  `raw_payload.description` is the CDK DESC column and therefore worth showing
 *  as an at-a-glance subline. Every other vendor puts whatever its scraper
 *  found there (Freightliner ships the Prop-65 cancer warning, CAT a generic
 *  "Description for part number …"), which is noise in a list view.
 *  decision: cdkdesc — see decisions/cdkdesc.md */
export const CDK_VENDOR_CODES: ReadonlySet<string> = new Set(['pacd', 'cupa']);

/** True when a check's `cdk_desc` is a real CDK DESC value rather than
 *  scraper-page boilerplate. */
export function hasCdkDesc(check: Pick<Check, 'vendor_code' | 'cdk_desc'>): boolean {
  return !!check.cdk_desc && CDK_VENDOR_CODES.has(check.vendor_code);
}

export const ALL_REASON_CODES: ReasonCode[] = [
  'PRICE_INCREASE',
  'PRICE_DECREASE',
  'CORE_CHARGE_ADDED',
  'CORE_CHARGE_REMOVED',
  'CORE_CHARGE_CHANGED',
  'IS_DISCONTINUED',
  'IS_SUPERSEDED',
  'NO_PRICE_INFO',
  'CDK_SSD',
  'CDK_MSS',
  'CDK_NLS',
  'CDK_RES',
  'CDK_POR',
  'CDK_IPL',
];

/** A vendor option for the filter multi-selects: raw code (query value) +
 *  client-facing name (label, from tbvendors.name; falls back to the code). */
export interface VendorOption {
  code: string;
  name: string;
}

export interface Check {
  id: number;
  vendor_code: string;
  /** Client-facing vendor label — tbvendors.name, falling back to the code. */
  vendor_name: string;
  ns_item_id: number;
  sku: string | null;
  our_price: number;
  vendor_price: number;
  /** Recalculated NS Special Price the push will write — null when the item has no active promo. */
  special_price: number | null;
  pct_diff: number;
  core_charge: boolean;
  core_charge_amount: number | null;
  /** NS-side core charge (tbnsdata.core_charge) — shown under Purchase cost;
   *  null when the item has no core charge in NetSuite (BOT-369). */
  ns_core_charge: number | null;
  status: CheckStatus;
  review_status: ReviewStatus;
  reason_code: ReasonCode | null;
  /** BOT-358: second reason when a check has two simultaneous changes — the
   *  core-charge change is the primary reason_code and the price direction is
   *  carried here. null when there is only one reason. */
  secondary_reason_code: ReasonCode | null;
  /** BOT-384: CDK replacement part number (raw_payload.pnc, pacd/cupa) — the
   *  successor PN for CDK_SSD supersessions. null for non-CDK vendors and on
   *  list views that don't select it (Queue / Processed). */
  cdk_pnc?: string | null;
  /** CDK DESC column (raw_payload.description, pacd/cupa) — exception text for
   *  coded parts ("CAN USE 00212235P"), plain part name otherwise. Max 40 chars
   *  (CDK fixed width). Same selection scope as cdk_pnc. */
  cdk_desc?: string | null;
  /** BOT-377: replacement SKU named by the vendor portal itself
   *  (raw_payload.superseded_by — fref/fre0's "Replaced by: X"). Set whenever the
   *  scrape found one, regardless of reason code: IS_DISCONTINUED outranks
   *  IS_SUPERSEDED, so a part that is both still carries its successor here.
   *  Same selection scope as cdk_pnc (Review + item history). */
  vendor_supersedure?: string | null;
  /** Remanufactured counterpart from the same payload (raw_payload.reman_code) —
   *  NOT a supersession: the original SKU can still be alive and priced. Only
   *  surfaced on dead parts (discontinued / superseded), where it is the
   *  operator's alternative to buy. */
  vendor_reman_code?: string | null;
  /** Live NS stock available to sell (tbnsdata.available = on hand minus
   *  committed, joined at read time) — not stock-at-check-time. null when the
   *  item is gone from tbnsdata, the ns_sync backfill hasn't run yet, or the
   *  list view doesn't select it. */
  ns_available?: number | null;
  /** tbnsdata.manufacturer, joined at read time — backs the hideable
   *  Manufacturer column on Review (BOT-392). Absent on Queue / Processed. */
  manufacturer?: string | null;
  /** tbnsdata.magento_id, joined at read time — the Magento entity id behind
   *  the "Open in Magento" row action (the NS item id is NOT a Magento id).
   *  null when the item has no Magento product — the action is hidden then.
   *  Absent on views without the action (item change history). */
  magento_id?: number | null;
  /** BOT-384: live Paccar Parts Online status (hf_paccar_portal_status, joined
   *  by SKU at read time; written nightly by hf-paccar-portal-sync). RED code
   *  exactly as the portal shows it (RSV, CNR, NSP, ...). null until the sync
   *  has checked this SKU, and on views that don't select it. */
  portal_dis_code?: string | null;
  /** The portal's ADDITIONAL DESCRIPTION text, boilerplate stripped — what the
   *  client reads there ("LIMITED TO 1PC PER DEALER PER DAY"). */
  portal_text?: string | null;
  /** Replacement candidate(s) from the portal's Exception popup, "; "-joined —
   *  covers MSS chains and SSD rows whose CDK file row had no PNC. */
  portal_supersedure?: string | null;
  /** When the portal was last checked for this SKU (ISO timestamp). */
  portal_checked_at?: string | null;
  reviewed_at: string | null;
  reviewer_email: string | null;
  screenshot_url: string | null;
  created_at: string | null;
  /** Scrape time ("Price Date"); null for rows written before the column existed. */
  price_date: string | null;
  ns_pushed_at: string | null;
  ns_push_error: string | null;
}

export interface ChecksPage {
  items: Check[];
  total: number;
  page: number;
  per_page: number;
}

export interface CheckFilters {
  /** One or more vendor codes (multi-select). */
  vendor_codes?: string[];
  status?: CheckStatus;
  review_status?: ReviewStatus;
  reason_codes?: ReasonCode[];
  /** tbnsdata.manufacturer — exact match (BOT-387). */
  manufacturer?: string;
  /** ISO dates (yyyy-mm-dd), inclusive on both ends. */
  price_date_from?: string;
  price_date_to?: string;
  pct_diff_min?: number;
  pct_diff_max?: number;
  our_price_min?: number;
  our_price_max?: number;
  page?: number;
  per_page?: number;
}

export async function fetchChecks(filters: CheckFilters = {}): Promise<ChecksPage> {
  const params = new URLSearchParams();
  if (filters.vendor_codes?.length) {
    filters.vendor_codes.forEach(vc => params.append('vendor_code', vc));
  }
  if (filters.status)        params.set('status', filters.status);
  if (filters.review_status) params.set('review_status', filters.review_status);
  if (filters.reason_codes?.length) {
    filters.reason_codes.forEach(rc => params.append('reason_code', rc));
  }
  if (filters.manufacturer) params.set('manufacturer', filters.manufacturer);
  if (filters.price_date_from) params.set('price_date_from', filters.price_date_from);
  if (filters.price_date_to)   params.set('price_date_to', filters.price_date_to);
  if (filters.pct_diff_min != null) params.set('pct_diff_min', String(filters.pct_diff_min));
  if (filters.pct_diff_max != null) params.set('pct_diff_max', String(filters.pct_diff_max));
  if (filters.our_price_min != null) params.set('our_price_min', String(filters.our_price_min));
  if (filters.our_price_max != null) params.set('our_price_max', String(filters.our_price_max));
  if (filters.page)          params.set('page', String(filters.page));
  if (filters.per_page)      params.set('per_page', String(filters.per_page));

  const res = await fetch(`/api/v1/checks?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Checks fetch failed: ${res.status}`);
  return res.json() as Promise<ChecksPage>;
}

export async function fetchCheckVendors(): Promise<VendorOption[]> {
  const res = await fetch('/api/v1/checks/vendor-codes', { credentials: 'include' });
  if (!res.ok) throw new Error(`Vendors fetch failed: ${res.status}`);
  const data = await res.json() as { vendors: VendorOption[] };
  return data.vendors;
}

/** Distinct manufacturers (tbnsdata.manufacturer) seen in checks — options for
 *  the manufacturer filter select on the Review page (BOT-387). */
export async function fetchCheckManufacturers(): Promise<string[]> {
  const res = await fetch('/api/v1/checks/manufacturers', { credentials: 'include' });
  if (!res.ok) throw new Error(`Manufacturers fetch failed: ${res.status}`);
  const data = await res.json() as { manufacturers: string[] };
  return data.manufacturers;
}

export type QueueActionType = 'price_sync' | 'auto_price_sync' | 'disable';

export async function fetchChecksQueue(
  filters: {
    vendor_codes?: string[];
    action_type?: QueueActionType;
    /** SAFE/UNSAFE check classification (checks.status). */
    status?: CheckStatus;
    queued_from?: string;
    queued_to?: string;
    page?: number;
    per_page?: number;
  } = {},
): Promise<ChecksPage> {
  const params = new URLSearchParams();
  if (filters.vendor_codes?.length) {
    filters.vendor_codes.forEach(vc => params.append('vendor_code', vc));
  }
  if (filters.action_type) params.set('action_type', filters.action_type);
  if (filters.status)      params.set('status', filters.status);
  if (filters.queued_from) params.set('queued_from', filters.queued_from);
  if (filters.queued_to)   params.set('queued_to', filters.queued_to);
  if (filters.page)        params.set('page', String(filters.page));
  if (filters.per_page)    params.set('per_page', String(filters.per_page));

  const res = await fetch(`/api/v1/checks/queue?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Checks queue fetch failed: ${res.status}`);
  return res.json() as Promise<ChecksPage>;
}

export async function fetchChecksProcessed(
  filters: {
    vendor_codes?: string[];
    review_status?: 'approved' | 'skipped' | 'disabled' | 'auto_approved';
    /** MVP "Type": SAFE/UNSAFE (check classification) or SKIPPED. */
    type?: 'safe' | 'unsafe' | 'skipped';
    price_date_from?: string;
    price_date_to?: string;
    synced_from?: string;
    synced_to?: string;
    page?: number;
    per_page?: number;
  } = {},
): Promise<ChecksPage> {
  const params = new URLSearchParams();
  if (filters.vendor_codes?.length) {
    filters.vendor_codes.forEach(vc => params.append('vendor_code', vc));
  }
  if (filters.review_status) params.set('review_status', filters.review_status);
  if (filters.type)          params.set('type', filters.type);
  if (filters.price_date_from) params.set('price_date_from', filters.price_date_from);
  if (filters.price_date_to)   params.set('price_date_to', filters.price_date_to);
  if (filters.synced_from)   params.set('synced_from', filters.synced_from);
  if (filters.synced_to)     params.set('synced_to', filters.synced_to);
  if (filters.page)          params.set('page', String(filters.page));
  if (filters.per_page)      params.set('per_page', String(filters.per_page));

  const res = await fetch(`/api/v1/checks/processed?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Checks processed fetch failed: ${res.status}`);
  return res.json() as Promise<ChecksPage>;
}

export async function reviewCheck(id: number, action: ReviewAction): Promise<void> {
  const res = await fetch(`/api/v1/checks/${id}/review`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  });
  if (!res.ok) throw await reviewRequestError(res, 'Review');
}

export async function bulkReview(ids: number[], action: ReviewAction): Promise<number> {
  const res = await fetch('/api/v1/checks/bulk-review', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, action }),
  });
  if (!res.ok) throw await reviewRequestError(res, 'Bulk review');
  const data = await res.json() as { updated: number };
  return data.updated;
}

export async function undoCheck(id: number): Promise<void> {
  const res = await fetch(`/api/v1/checks/queue/${id}/undo`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Undo failed: ${res.status}`);
}
